import { Fragment, useState } from 'react';
import { isNotNullish } from '@chainflip/utils/guard';
import BigNumber from 'bignumber.js';
import classNames from 'classnames';
import { motion } from 'motion/react';
import GreenSpinnerJSON from '@/shared/animations/spinner-green.json';
import { type ChainflipToken } from '@/shared/assets/tokens';
import Pill from '@/shared/components/flip-ui-kit/Pill';
import { TokenLogo } from '@/shared/components/logo/TokenLogo';
import { TokenWithChainByAsset } from '@/shared/components/logo/TokenWithChain';
import { Tooltip } from '@/shared/components/molecules/Tooltip';
import QuestionMarkTooltip from '@/shared/components/QuestionMarkTooltip';
import { Rate } from '@/shared/components/Rate';
import RouteTooltip from '@/shared/components/RouteTooltip';
import { DurationBadge } from '@/shared/components/swap-flow/RouteList/DurationBadge';
import MarketData from '@/shared/components/swap-flow/RouteList/MarketData';
import { PlatformFees } from '@/shared/components/swap-flow/RouteList/PlatformFees';
import { UsdAmount } from '@/shared/components/UsdAmount';
import useBoolean from '@/shared/hooks/useBoolean';
import { useCoinGeckoPrices } from '@/shared/hooks/useCoinGeckoPrices';
import usePriceDelta from '@/shared/hooks/usePriceDelta';
import useScreenSize from '@/shared/hooks/useScreenSize';
import useSettingsStore from '@/shared/hooks/useSettingsStore';
import useSwapRequestStore, {
  selectAutoLivePriceSlippageTolerancePercent,
  selectAutoSlippageTolerancePercent,
} from '@/shared/hooks/useSwapRequestStore';
import useTokenPrice from '@/shared/hooks/useTokenPrice';
import ArrowRightIcon from '@/shared/icons/ArrowRight';
import ChevronIcon from '@/shared/icons/flip-ui-kit/large/Chevron';
import EmojiSadIcon from '@/shared/icons/flip-ui-kit/large/EmojiSad';
import Chevron from '@/shared/icons/flip-ui-kit/small/Chevron';
import ChunksIcon from '@/shared/icons/flip-ui-kit/small/Chunks';
import DollarIcon from '@/shared/icons/flip-ui-kit/small/Dollar';
import FeeIcon from '@/shared/icons/flip-ui-kit/small/Fee';
import GlobeIcon from '@/shared/icons/flip-ui-kit/small/Globe';
import RouteIcon from '@/shared/icons/flip-ui-kit/small/Route';
import ShieldTickIcon from '@/shared/icons/flip-ui-kit/small/ShieldTick';
import SliceIcon from '@/shared/icons/flip-ui-kit/small/Slice';
import TimeSpeedIcon from '@/shared/icons/flip-ui-kit/small/TimeSpeed';
import { integrationManager, type RouteResponse } from '@/shared/integrations';
import { calculateMinPrice, calculateMinReceived } from '@/shared/integrations/chainflip';
import Lottie from '@/shared/utils/Lottie';
import { formatUsdValue } from '@/shared/utils/numbers';

const RouteSection = ({ route, disabled }: { route: RouteResponse; disabled: boolean }) => {
  const [showRouteDetails, setShowRouteDetails] = useState<boolean>(false);

  if (route.integration === 'chainflip') {
    const sourceAsset = route.srcToken.chainflipId;
    const destinationAsset = route.destToken.chainflipId;
    return (
      <Tooltip
        content={
          <RouteTooltip
            type="Route"
            swapRequest={{
              sourceAsset,
              destinationAsset,
              executedSwaps: {
                totalCount: 1,
                aggregates: {
                  sum: {
                    swapInputAmount: route.srcAmount.toString(),
                    swapInputValueUsd: '0',
                    intermediateAmount: route.steps[1]?.srcAmount.toString() ?? '0',
                    intermediateValueUsd: '0',
                    swapOutputAmount: route.destAmount.toString(),
                    swapOutputValueUsd: '0',
                  },
                },
              },
            }}
          />
        }
      >
        <div className="flex items-center gap-x-1">
          {route.steps.map((step, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <span key={`${route.id}-${index}`} className="flex items-center gap-x-1">
              <TokenLogo token={step.srcToken} />
              <ArrowRightIcon width="16px" height="16px" className="text-cf-light-1" />
              {index === route.steps.length - 1 && <TokenLogo token={step.destToken} />}
            </span>
          ))}
        </div>
      </Tooltip>
    );
  }

  return (
    <>
      <div className="flex items-center justify-around">
        <div className="flex items-center justify-around space-x-1">
          {route.steps.map((step, index) => (
            <Fragment key={step.protocolName + step.srcToken.symbol}>
              {step.srcToken.chainflipId ? (
                <TokenWithChainByAsset
                  asset={step.srcToken.chainflipId}
                  displayChainLogo={index !== 1}
                />
              ) : (
                <TokenLogo token={step.srcToken} />
              )}
              <ArrowRightIcon className="text-cf-light-1" width="16px" height="16px" />
              {index === route.steps.length - 1 &&
                (step.destToken.chainflipId ? (
                  <TokenWithChainByAsset
                    asset={step.destToken.chainflipId}
                    displayChainLogo={index !== 1}
                  />
                ) : (
                  <TokenLogo token={step.destToken} />
                ))}
            </Fragment>
          ))}
        </div>

        <button
          type="button"
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            setShowRouteDetails(!showRouteDetails);
          }}
        >
          <Chevron
            flip={!showRouteDetails}
            className="cursor-pointer text-cf-light-3 duration-100"
          />
        </button>
      </div>

      <motion.div
        className="!mt-0 h-0 overflow-hidden font-aeonikMedium text-13 text-cf-light-2"
        animate={showRouteDetails ? 'open' : 'closed'}
        variants={{
          open: { height: 'auto', opacity: 1 },
          closed: { height: '0px', opacity: 0 },
        }}
        transition={{ duration: 0.15 }}
      >
        <div className="mt-2 flex flex-col space-y-2">
          {route.steps.map((step) => (
            <div key={step.protocolName + step.srcToken.symbol}>
              <div className="ml-1 flex items-end space-x-1">
                <div className="text-cf-light-2">
                  Swap <span className="font-semibold">{step.srcAmount.toCapped()}</span>{' '}
                  {step.srcToken.symbol} to{' '}
                  <span className="font-semibold">{step.destAmount.toCapped()}</span>{' '}
                  {step.destToken.symbol} via {step.protocolName}
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </>
  );
};

export const RouteCardLoading = () => (
  <div className="flex h-[334px] items-center justify-center space-y-3 rounded-md border border-cf-gray-4 bg-cf-gray-3 p-5 text-14 text-cf-light-1 backdrop-blur-[6px] duration-150">
    <div className="flex items-center">
      <Lottie as="div" className="mr-2 h-5 w-5" animationData={GreenSpinnerJSON} autoplay loop />
      <span>Fetching routes...</span>
    </div>
  </div>
);
export const RouteCardNotFound = () => (
  <div className="flex h-[334px] cursor-pointer items-center justify-center space-y-3 p-5 duration-150">
    <div className="flex flex-col items-center justify-center space-y-1">
      <EmojiSadIcon className="text-cf-light-2" width={60} height={60} />
      <span className="px-20 text-center text-14 text-cf-light-2">
        No routes available at this time. Please try a different pair.
      </span>
    </div>
  </div>
);

type RouteCardProps = {
  route: RouteResponse;
  onRouteSelected: (route: RouteResponse) => void;
  isSelected?: boolean;
  differenceFromWorstRoute?: string | undefined;
  disabled?: boolean;
  secondsUntilNextRefresh: string;
  fastest: boolean;
  bestValue: boolean;
};

const RouteCard = ({
  route,
  onRouteSelected,
  isSelected = false,
  differenceFromWorstRoute,
  disabled = false,
  secondsUntilNextRefresh,
  fastest,
  bestValue,
}: RouteCardProps) => {
  const { isMobile } = useScreenSize();
  const { getPrice } = useCoinGeckoPrices();
  const integrationName = integrationManager.getName(route.integration);
  const Logo = integrationManager.getLogo(route.integration);
  const { price: destTokenPrice } = useTokenPrice(route.destToken);
  const { delta, color } = usePriceDelta(route);
  const { slippageMode, slippageTolerancePercent, livePriceSlippageTolerancePercent } =
    useSettingsStore();
  const { toggle: toggleDetails, value: displayDetails } = useBoolean(isSelected);
  const isOnChainSwap = useSettingsStore((state) => state.isOnChainSwap);

  const autoLivePriceSlippageTolerancePercent = useSwapRequestStore(
    selectAutoLivePriceSlippageTolerancePercent,
  );
  const autoSlippageTolerancePercent = useSwapRequestStore(selectAutoSlippageTolerancePercent);

  let minReceived;
  if (route.integration === 'chainflip') {
    const effectiveSlippageTolerancePercent =
      slippageMode === 'auto' ? autoSlippageTolerancePercent : slippageTolerancePercent;

    minReceived = calculateMinReceived(
      route,
      calculateMinPrice(route.integrationData.quote, effectiveSlippageTolerancePercent),
    );
  }

  const numberOfChunks =
    route.integration === 'chainflip' && route.integrationData.dcaParams?.numberOfChunks;

  const totalPlatformFeeUsd = BigNumber.sum(
    ...route.platformFees.map((fee) => fee.amount.mul(getPrice(fee.token) ?? 0).toBigNumber()),
    0,
  );

  const selectedLivePriceProtectionTolerancePercent =
    autoLivePriceSlippageTolerancePercent &&
    (slippageMode === 'auto'
      ? autoLivePriceSlippageTolerancePercent
      : livePriceSlippageTolerancePercent);

  const selectedSlippageTolerancePercent =
    slippageMode === 'auto' ? autoSlippageTolerancePercent : slippageTolerancePercent;

  const effectiveSlippageTolerancePercent =
    selectedLivePriceProtectionTolerancePercent ?? selectedSlippageTolerancePercent;

  const getSlippageTooltipContent = () => {
    if (slippageMode === 'auto') {
      return isNotNullish(autoLivePriceSlippageTolerancePercent)
        ? 'Recommended Live Price Slippage Tolerance based on the current oracle price and liquidity'
        : 'Recommended Max. Slippage Tolerance based on the current market conditions';
    }
    return isNotNullish(autoLivePriceSlippageTolerancePercent)
      ? 'Your custom Live Price Slippage Tolerance'
      : 'Your custom Max. Slippage Tolerance';
  };

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={numberOfChunks ? 'dca-route-card' : 'regular-route-card'}
      className={classNames('w-full space-y-3 rounded-md border p-4 pb-0 duration-150', {
        'border-cf-gray-4 bg-cf-gray-3': disabled,
        'border-cf-gray-5 bg-[#2C2C2C] shadow-[0px_0px_0px_3px_rgba(255,255,255,0.04),0px_0px_14px_0px_rgba(0,0,0,0.32),3px_3px_18px_0px_rgba(0,0,0,0.16)_inset]':
          isSelected,
        'border-cf-gray-4 bg-cf-gray-3-5 shadow-grayPop2 backdrop-blur-[6px] hover:border-cf-gray-5 hover:bg-[#2C2C2C]':
          !isSelected,
      })}
      onClick={() => {
        if (isSelected) {
          toggleDetails();
        }
        onRouteSelected(route);
      }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center justify-between space-x-1">
          {bestValue && (
            <Pill
              text="Best Value"
              color={isSelected ? 'white' : 'neutral'}
              Icon={DollarIcon}
              iconColor="text-cf-green-3"
            />
          )}
          {fastest && (
            <Pill
              text="Faster"
              color={isSelected ? 'white' : 'neutral'}
              Icon={TimeSpeedIcon}
              iconColor="text-cf-blue-4"
            />
          )}
          {numberOfChunks && (
            <Pill
              text={`${numberOfChunks} chunks`}
              color={isSelected ? 'white' : 'neutral'}
              Icon={ChunksIcon}
            />
          )}
        </div>
        <div className="flex items-center gap-x-1">
          {!isMobile && (
            <MarketData
              disabled={disabled}
              route={route}
              spinner={
                <span className="text-12 text-cf-light-2">({secondsUntilNextRefresh}s)</span>
              }
              className="rounded-md border border-cf-gray-5 bg-cf-gray-4 text-cf-light-3 enabled:hover:border-cf-light-1 enabled:hover:bg-cf-gray-5 enabled:hover:text-cf-white"
            />
          )}
          <button
            type="button"
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              toggleDetails();
            }}
          >
            <ChevronIcon
              flip={!displayDetails}
              className={classNames(
                'rounded-md border border-cf-gray-5 bg-cf-gray-4 text-cf-light-3 hover:border-cf-light-1 hover:bg-cf-gray-5 hover:text-cf-white',
                displayDetails && 'border-cf-light-1 bg-cf-gray-5',
              )}
            />
          </button>
        </div>
      </div>

      <div className="flex items-center justify-start space-x-3">
        <div>
          <div className="flex items-center justify-start space-x-1">
            <span className="text-20 text-white">
              {route.destAmount.toCapped()} {route.destToken.symbol}
            </span>
            {differenceFromWorstRoute && Number(differenceFromWorstRoute) > 0 && (
              <span className="text-13 text-cf-green-3">
                (+{differenceFromWorstRoute} {route.destToken.symbol})
              </span>
            )}
          </div>
          <div className="flex items-center justify-start gap-x-1 text-13 text-cf-light-2">
            {destTokenPrice && (
              <>
                <Tooltip
                  pointer={false}
                  content="The difference between the global index price and the estimated rate"
                >
                  <span>
                    <UsdAmount token={route.destToken} tokenAmount={route.destAmount} />
                    {delta && <span className={color}> ({delta.toFixed(1)}%)</span>}
                  </span>
                </Tooltip>
                <span>&nbsp;·</span>
              </>
            )}
            <Tooltip pointer={false} content={getSlippageTooltipContent()}>
              <div className="flex flex-row items-center gap-0.5">
                <ShieldTickIcon
                  className={classNames(
                    isNotNullish(autoLivePriceSlippageTolerancePercent) && 'text-cf-green-3',
                  )}
                />
                {slippageMode === 'auto'
                  ? `Auto ${effectiveSlippageTolerancePercent}%`
                  : `${effectiveSlippageTolerancePercent}%`}
              </div>
            </Tooltip>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="text-13">
          <Rate
            depositAmount={route.srcAmount.toString()}
            sourceAsset={(route.srcToken as ChainflipToken).chainflipId}
            egressAmount={route.destAmount.toString()}
            destinationAsset={(route.destToken as ChainflipToken).chainflipId}
            decimalCap={5}
          />
        </div>
        <div className="flex items-center gap-x-2">
          <div className="flex items-center gap-x-1 font-aeonikMedium text-13 text-cf-light-3">
            <FeeIcon className="text-cf-light-3" />
            {formatUsdValue(totalPlatformFeeUsd)}
          </div>
          <span>{route.durationSeconds > 0 && <DurationBadge route={route} />}</span>
        </div>
      </div>
      <motion.div
        className="!mt-4 h-0 overflow-hidden font-aeonikMedium text-13 text-cf-light-2"
        animate={displayDetails ? 'open' : 'closed'}
        variants={{
          open: { height: 'auto', opacity: 1 },
          closed: { height: '0px', opacity: 0 },
        }}
        transition={{ duration: 0.15 }}
      >
        <div
          className={classNames(
            'flex flex-col gap-y-3 border-t border-cf-gray-5 pt-3',
            !isOnChainSwap && 'py-3',
          )}
        >
          <PlatformFees fees={route.platformFees} />
        </div>
        <div
          className={classNames(
            'flex flex-col gap-y-3 py-3 pb-5',
            !isOnChainSwap && 'border-t border-cf-gray-5',
          )}
        >
          {minReceived && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-x-2">
                <ShieldTickIcon /> Min. payout
                <QuestionMarkTooltip
                  content="Minimum amount received at the destination address based on slippage tolerance
                      and estimated fees"
                  link="https://docs.chainflip.io/protocol/swapping-basics#minimum-accepted-price"
                />
              </div>
              <div className="text-cf-white">
                {minReceived?.toCapped()} {route.destToken.symbol}
              </div>
            </div>
          )}
          {numberOfChunks && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-x-2">
                <SliceIcon /> Chunks
                <QuestionMarkTooltip
                  content="Chunks are portions of your deposit amount that are split up and swapped over time"
                  link="https://docs.chainflip.io/protocol/swapping-basics#dollar-cost-average-dca-improving-price"
                />
              </div>
              <div className="text-white">{numberOfChunks}</div>
            </div>
          )}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-x-2">
              <RouteIcon /> Route
            </div>
            <div>
              <RouteSection route={route} disabled={disabled} />
            </div>
          </div>
          {integrationManager.integrationCount > 1 && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-x-2">
                <GlobeIcon /> Protocol
              </div>
              <div>
                <div className="flex items-center justify-start space-x-1">
                  <Logo width={13} height={13} />
                  <span className="font-aeonikMedium">{integrationName}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default RouteCard;
